// Groupe de menus « drop-up » (barre du bas) : un seul ouvert à la fois, fermé
// par un clic en dehors, par un clic sur une de ses entrées et au départ de
// toute visite.
import { useCallback, useEffect, useRef, useState } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'
import { router } from '@inertiajs/react'

export function useDropup<K extends string>() {
  const [open, setOpen] = useState<K | null>(null)
  const containers = useRef(new Map<K, HTMLElement>())

  useEffect(() => {
    if (open === null) return
    const onClick = (event: MouseEvent) => {
      const container = containers.current.get(open)
      if (container && !container.contains(event.target as Node)) setOpen(null)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [open])

  useEffect(
    () =>
      router.on('start', (event) => {
        if (event.detail.visit.prefetch || event.detail.visit.async) return
        setOpen(null)
      }),
    [],
  )

  const toggle = useCallback((key: K, event: ReactMouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setOpen((current) => (current === key ? null : key))
  }, [])

  const close = useCallback(() => setOpen(null), [])

  // Élément racine d'un menu (le clic « en dehors » se mesure par rapport à lui).
  const container = useCallback(
    (key: K) => (element: HTMLElement | null) => {
      if (element) containers.current.set(key, element)
      else containers.current.delete(key)
    },
    [],
  )

  return { open, toggle, close, container }
}
